/**
 * LAGOSContext — Single source of truth for Lagos State platform.
 * Feeds ALL 13 pages from one consolidated endpoint.
 * This is NOT a corporate dashboard — it's a state aggregation engine.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import client from "@/api/client";

const Ctx = createContext(null);
export const useLagos = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLagos must be used within LAGOSProvider");
  return ctx;
};

export function LAGOSProvider({ children }) {
  const [periods,        setPeriods]        = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [consolidated,   setConsolidated]   = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [cpaPoaData,     setCpaPoaData]     = useState(null);

  useEffect(() => {
    client.get("/lagos/periods").then(r => {
      const ps = r.data || [];
      setPeriods(ps);
      if (ps[0]) setSelectedPeriod(ps[0].period_id);
    }).catch(() => setLoading(false));
  }, []);

  const loadConsolidated = useCallback((pid) => {
    if (!pid) return;
    setLoading(true);
    Promise.all([
      client.get(`/lagos/consolidated/${pid}`),
      client.get(`/lagos/cpa-poa/${pid}`),
    ]).then(([c, cp]) => {
      setConsolidated(c.data);
      setCpaPoaData(cp.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (selectedPeriod) loadConsolidated(selectedPeriod); }, [selectedPeriod, loadConsolidated]);

  const refresh = useCallback(() => { if (selectedPeriod) loadConsolidated(selectedPeriod); }, [selectedPeriod, loadConsolidated]);

  return (
    <Ctx.Provider value={{
      periods, selectedPeriod, setSelectedPeriod,
      consolidated, loading, refresh,
      cpaPoaData,
      portfolio: consolidated?.portfolio || {},
      sdg:       consolidated?.sdg || [],
    }}>
      {children}
    </Ctx.Provider>
  );
}
